'use client'

import { type ComponentProps, type ReactNode, useLayoutEffect, useRef, useState } from 'react'

import { cn } from '../../utils/cn'
import { Input } from '../_base/Input'

const DEFAULT_PADDING = 12

interface InputWithExtraContentProps extends Omit<ComponentProps<typeof Input>, 'children'> {
  extraContent: ReactNode
  extraContentPadding?: number
}

export function InputWithExtraContent({
  extraContent,
  extraContentPadding = DEFAULT_PADDING,
  className,
  style,
  ...inputProps
}: InputWithExtraContentProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [contentWidth, setContentWidth] = useState(0)

  useLayoutEffect(() => {
    if (!contentRef.current) return

    const updateWidth = () => {
      setContentWidth(contentRef.current?.offsetWidth ?? 0)
    }

    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(contentRef.current)

    return () => resizeObserver.disconnect()
  }, [])

  return (
    <div className="relative">
      <Input
        className={cn('relative z-10', className)}
        style={{
          ...style,
          paddingRight: `${contentWidth + extraContentPadding}px`
        }}
        {...inputProps}
      />

      <div
        ref={contentRef}
        className="absolute top-1/2 right-2 z-20 -translate-y-1/2 overflow-hidden"
      >
        {extraContent}
      </div>
    </div>
  )
}
